"use client"; // Note: This directive is Next.js-specific and might not be needed in plain React, depending on your setup

import React, { useState } from "react";

import { ArrowLeft, ArrowRight, Pen } from "lucide-react";
import { Button } from "../../components/ui/button";
import Header from "../../layouts/Header";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import SelectTimeout from "../../components/SelectTimeout";
import Disable2FASettings from "../../components/Disable2FASettings";
import PasswordChange from "../../components/PasswordChange";
import AntiPhishing from "../../components/AntiPhishing";
import Disable2Fa from "../../components/Disable2FaDialog";
import Enable2Fa from "../../components/Enable2FaDialog";
import Toaster from "../../components/ui/toaster";

const SecurityPageMain = () => {
  const { settings } = useAuth();
  const navigate = useNavigate();
  const [pageNo, setPageNo] = useState(0);
  const [twoFaType, setTwoFaType] = useState("");

  function loginTwoFa(data) {
    setTwoFaType(data);
    setPageNo(4);
  }

  return (
    <>
      <Header />
      <Toaster />
      <div className="min-h-screen w-full bg-gray-100 flex flex-col">
        <div className="h-14 bg-gradient-to-r from-blue-600 to-purple-600 flex items-center pl-20 text-white">
          <ArrowLeft
            className="mr-5 bg-transparent rounded-full p-1 border-gray-200/60 border hover:border-gray-400 hover:bg-white/20"
            onClick={() => {
              if (pageNo === 0) {
                navigate("/settings/index");
              } else {
                setPageNo(0);
              }
            }}
          />
          <p className="text-sm font-medium underline mr-2">Settings</p>
          {(() => {
            if (pageNo === 0) {
              return <p className="text-sm font-medium">/ Security</p>;
            } else if (pageNo === 2) {
              return (
                <p className="text-sm font-medium">/ Security / Password</p>
              );
            } else if (pageNo === 3) {
              return (
                <p className="text-sm font-medium">
                  / Security / Anti-phishing phrase
                </p>
              );
            } else if (pageNo === 4) {
              return (
                <p className="text-sm font-medium">
                  / Security / 2FA Configure
                </p>
              );
            }
          })()}
        </div>
        {(() => {
          if (pageNo === 0) {
            return (
              <div className="flex justify-center mt-8 px-4 md:px-16 lg:px-32">
                <div className="max-w-3xl w-full">
                  <Card className="bg-white rounded-2xl shadow-sm overflow-hidden">
                    <CardHeader className="px-6 pt-6 pb-4">
                      <CardTitle className="text-2xl font-semibold text-text-secondary text-[20px]">
                        Security
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="px-6 pb-4 space-y-1">
                      <div className="pb-3">
                        <p className="font-semibold text-text-muted text-[14px] mb-3">
                          Signing in
                        </p>
                        <div className="flex justify-between items-center border-solid border-[1px] rounded-[20px] border-gray-200 py-5 px-4 mb-2">
                          <div>
                            <p className="font-semibold text-text-primary text-[16px]">
                              Two-factor authentication
                            </p>
                            <p className="font-normal text-text-secondary text-[14px] mt-1">
                              {settings?.security?.twoFactorAuth?.enabled ===
                              false ? (
                                <span className="text-accent-red">
                                  Not configured
                                </span>
                              ) : (
                                "Configured"
                              )}
                            </p>
                          </div>
                          {settings?.security?.twoFactorAuth?.enabled ===
                          false ? (
                            <Enable2Fa />
                          ) : (
                            <Disable2Fa
                              setPageNo={setPageNo}
                              setTwoFaType={setTwoFaType}
                            />
                          )}
                        </div>
                      </div>
                      <div className="pb-3">
                        <div className="flex justify-between items-center border-solid border-[1px] rounded-[20px] border-gray-200 py-5 px-4 mb-2">
                          <div>
                            <p className="font-semibold text-text-primary text-[16px]">
                              Two-factor login
                            </p>
                            <p className="font-normal text-text-secondary text-[14px] mt-1">
                              {settings?.security?.twoFaLogin === false ? (
                                <span className="text-accent-red">
                                  Disabled
                                </span>
                              ) : (
                                "Enabled"
                              )}
                            </p>
                          </div>
                          {settings?.security?.twoFaLogin === false ? (
                            <Button
                              disabled={
                                settings?.security?.twoFactorAuth?.enabled ===
                                false
                              }
                              variant="ghost"
                              className="relative w-30 h-10 rounded-full bg-transparent  border border-gray-100 border-opacity-35 text-text-secondary  flex justify-between px-4"
                              onClick={() => loginTwoFa("login-on")}
                            >
                              <span className="text-sm font-semibold">
                                Enable 2FA Login
                              </span>
                              <ArrowRight className="w-4 h-4 text-gray-500 " />
                            </Button>
                          ) : (
                            <Button
                              onClick={() => loginTwoFa("login-off")}
                              variant="ghost"
                              className="relative w-30 h-10 rounded-full bg-transparent  border border-gray-100 border-opacity-35 text-text-secondary  flex justify-between px-4"
                            >
                              <span className="text-sm font-semibold">
                                Disable 2FA Login
                              </span>
                              <ArrowRight className="w-4 h-4 text-gray-500 " />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="pb-3">
                        <p className="font-semibold text-text-muted text-[14px] mb-3">
                          Accessing your account
                        </p>
                        <div className="flex justify-between items-center border-solid border-[1px] rounded-[20px] border-gray-200 py-5 px-4 mb-2">
                          <div>
                            <p className="font-semibold text-text-primary text-[16px] ">
                              Auto-sign out
                            </p>
                            <p className="font-normal text-text-secondary text-[14px] mt-1">
                              {settings?.security?.autoLogoutDuration}
                            </p>
                          </div>
                          <SelectTimeout
                            timeOut={settings?.security?.autoLogoutDuration}
                          />
                        </div>
                      </div>

                      <div className="pb-3">
                        <p className="font-semibold text-text-muted text-[14px] mb-3">
                          Additional security
                        </p>
                        <div className="flex justify-between items-center border-solid border-[1px] rounded-[20px] border-gray-200 py-5 px-4 mb-2">
                          <div>
                            <p className="font-semibold text-text-primary text-[16px] ">
                              Anti-phishing phrase
                            </p>
                            <p className="font-normal text-text-secondary text-[14px] mt-1">
                              {settings?.security?.antiPhishCode?.enabled ===
                              false ? (
                                <span className="text-accent-red">
                                  Not configured
                                </span>
                              ) : (
                                "Configured"
                              )}
                            </p>
                          </div>
                          {settings?.security?.antiPhishCode?.enabled ===
                          false ? (
                            <>
                              <Button
                                variant="ghost"
                                className="relative w-30 h-10 rounded-full bg-transparent  border border-gray-100 border-opacity-35 text-text-secondary  flex justify-between px-4"
                                onClick={() => {
                                  setPageNo(3);
                                }}
                              >
                                <span className="text-sm font-semibold">
                                  Configure
                                </span>
                                <ArrowRight className="w-4 h-4 text-gray-500 " />
                              </Button>
                            </>
                          ) : (
                            <>
                              <div className="flex">
                                <div
                                  className="mr-2 rounded-full p-[8px] border cursor-pointer hover:bg-gray-50"
                                  onClick={() => {
                                    setPageNo(3);
                                  }}
                                >
                                  <Pen size={16} strokeWidth={1} />
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            );
          } else if (pageNo === 2) {
            return <PasswordChange />;
          } else if (pageNo === 3) {
            return <AntiPhishing setPageNo={setPageNo} />;
          } else if (pageNo === 4) {
            return (
              <Disable2FASettings setPageNo={setPageNo} twoFaType={twoFaType} />
            );
          }
        })()}
      </div>
    </>
  );
};

export default SecurityPageMain;
